import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Navigate } from "react-router-dom";
import { Trash, Star, Receipt, ShieldCheck, Crown, X, Briefcase, Envelope, Storefront, Headset, Gift, Newspaper, UserPlus } from "@phosphor-icons/react";
import PaymentsAdmin from "./PaymentsAdmin";
import ReportersAdmin from "./ReportersAdmin";

function FeaturedAdmin() {
  const [index, setIndex] = useState({ gigs: [], events: [], businesses: [], ads: [] });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ kind: "gig", id: "", days: 7 });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // Typeahead state for the grant form.
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feature/index");
      setIndex(data);
    } catch (e) {
      setMsg({ tone: "err", text: "Failed to load featured items." });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Debounced typeahead — also re-fires when the kind changes so the dropdown
  // matches the currently selected collection.
  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/feature/search", { params: { kind: form.kind, q: query } });
        if (!cancelled) setResults(data.items || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, form.kind]);

  const grant = async (e) => {
    e?.preventDefault?.();
    setMsg(null);
    const id = (form.id || "").trim();
    const days = parseInt(form.days, 10);
    if (!id) { setMsg({ tone: "err", text: "Enter the ID of the item to feature." }); return; }
    if (!Number.isFinite(days) || days < 1 || days > 365) { setMsg({ tone: "err", text: "Days must be between 1 and 365." }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/feature/grant", { kind: form.kind, id, days });
      setMsg({ tone: "ok", text: `Featured ${form.kind} until ${new Date(data.featured_until).toLocaleString()}.` });
      setForm({ ...form, id: "" });
      load();
    } catch (e) {
      setMsg({ tone: "err", text: e?.response?.data?.detail || "Grant failed." });
    } finally {
      setBusy(false);
    }
  };

  const unfeature = async (kind, id) => {
    if (!window.confirm(`Remove featured status from this ${kind}?`)) return;
    try {
      await api.post("/admin/feature/unfeature", { kind, id });
      load();
    } catch (e) {
      setMsg({ tone: "err", text: e?.response?.data?.detail || "Unfeature failed." });
    }
  };

  const groups = [
    { kind: "gig", title: "Featured Jobs", color: "border-[#BEE7F0]/40", items: index.gigs, idKey: "gig_id", labelKey: "title", subKey: "owner_name" },
    { kind: "event", title: "Featured Events", color: "border-[#D4AF37]/40", items: index.events, idKey: "event_id", labelKey: "title", subKey: "host_name" },
    { kind: "business", title: "Featured Businesses", color: "border-[#FFD700]/40", items: index.businesses, idKey: "business_id", labelKey: "name", subKey: "owner_name" },
    { kind: "ad", title: "Sponsored Ads", color: "border-[#F2DC8C]/40", items: index.ads, idKey: "ad_id", labelKey: "headline", subKey: "owner_name" },
  ];

  return (
    <div className="space-y-6">
      {/* Grant form */}
      <div className="card-sharp p-5" data-testid="featured-grant-card">
        <div className="flex items-center gap-2 mb-3">
          <Gift size={18} className="text-neon-lime" weight="fill"/>
          <div className="font-display text-lg">Gift featured / sponsored days</div>
        </div>
        <div className="text-[11px] text-muted-foreground mb-4 leading-relaxed">
          Manually grant a member's job, event, business, or ad N days of premium placement — no Stripe charge. The owner gets a notification.
        </div>
        <form onSubmit={grant} className="grid grid-cols-1 md:grid-cols-[140px_1fr_120px_auto] gap-2">
          <select
            data-testid="featured-grant-kind"
            value={form.kind}
            onChange={e => setForm({ ...form, kind: e.target.value })}
            className="input-sharp"
          >
            <option value="gig">Job</option>
            <option value="event">Event</option>
            <option value="business">Business</option>
            <option value="ad">Ad</option>
          </select>
          <input
            data-testid="featured-grant-id"
            className="input-sharp"
            placeholder={`Search ${form.kind}s by title or owner — or paste an ID`}
            value={query || form.id}
            onChange={e => {
              const v = e.target.value;
              setQuery(v);
              setForm({ ...form, id: v.startsWith(form.kind === "business" ? "biz_" : `${form.kind.slice(0,3)}_`) ? v : form.id });
              setResultsOpen(true);
            }}
            onFocus={() => setResultsOpen(true)}
            onBlur={() => setTimeout(() => setResultsOpen(false), 150)}
            autoComplete="off"
          />
          <input
            data-testid="featured-grant-days"
            type="number"
            min="1"
            max="365"
            className="input-sharp"
            placeholder="Days"
            value={form.days}
            onChange={e => setForm({ ...form, days: e.target.value })}
          />
          <button
            type="submit"
            data-testid="featured-grant-submit"
            disabled={busy}
            className="btn-neon disabled:opacity-50"
          >{busy ? "Granting…" : "Grant"}</button>
        </form>
        {msg && (
          <div data-testid="featured-grant-msg" className={`text-xs mt-3 ${msg.tone === "ok" ? "text-neon-lime" : "text-neon-pink"}`}>
            {msg.text}
          </div>
        )}
      </div>

      {/* Currently featured */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Currently featured (admin grants + paid boosts)
          </div>
          <button
            data-testid="featured-refresh"
            onClick={load}
            disabled={loading}
            className="text-[10px] uppercase tracking-widest text-neon-cyan hover:text-neon-pink disabled:opacity-40"
          >{loading ? "Refreshing…" : "Refresh"}</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {groups.map(g => (
            <div key={g.kind} className={`card-sharp p-4 ${g.color}`}>
              <div className="font-display text-sm mb-2">{g.title} <span className="text-[10px] text-muted-foreground">· {g.items.length}</span></div>
              {g.items.length === 0 && <div className="text-[11px] text-muted-foreground italic">None featured right now.</div>}
              <div className="space-y-1.5">
                {g.items.map(it => (
                  <div key={it[g.idKey]} data-testid={`featured-row-${it[g.idKey]}`} className="flex items-start justify-between gap-2 text-xs border border-white/10 p-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-display">{it[g.labelKey] || "—"}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {it[g.subKey] ? `${it[g.subKey]} · ` : ""}{it[g.idKey]}
                      </div>
                      <div className="text-[10px] text-neon-lime mt-0.5">until {new Date(it.featured_until).toLocaleString()}</div>
                    </div>
                    <button
                      data-testid={`featured-remove-${it[g.idKey]}`}
                      onClick={() => unfeature(g.kind, it[g.idKey])}
                      title="Unfeature"
                      className="text-muted-foreground hover:text-neon-pink p-1"
                    ><X size={12}/></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AgentAppsAdmin() {
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [agents, setAgents] = useState([]);
  const [picked, setPicked] = useState({});
  const [roster, setRoster] = useState({ agents: [], non_agents: [] });
  const [rosterQ, setRosterQ] = useState("");
  const loadRoster = async (q = "") => {
    try {
      const { data } = await api.get(`/admin/agent-roster${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRoster(data);
    } catch (e) { console.error("roster load failed", e); }
  };
  const load = async () => {
    try { const { data } = await api.get("/admin/agent-applications"); setRows(data); } catch (e) { console.error(e); }
    try { const { data } = await api.get("/admin/agent-requests"); setRequests(data.requests || []); setAgents(data.agents || []); } catch (e) { console.error(e); }
    loadRoster(rosterQ);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const setAgentStatus = async (userId, name, makeAgent) => {
    const verb = makeAgent ? "Grant" : "Revoke";
    if (!window.confirm(`${verb} Agent status for ${name || userId}?`)) return;
    try {
      await api.post(`/admin/users/${userId}/agent`, { is_agent: makeAgent });
      load();
    } catch (e) { console.error("set agent failed", e); }
  };
  const approve = async (id) => {
    if (!window.confirm("Approve this application? This user will become an Agent.")) return;
    await api.post(`/admin/agent-applications/${id}/approve`); load();
  };
  const deny = async (id) => {
    if (!window.confirm("Deny this application?")) return;
    await api.post(`/admin/agent-applications/${id}/deny`); load();
  };
  const assign = async (reqId, userName) => {
    const agentId = picked[reqId];
    if (!agentId) { window.alert("Pick an agent first."); return; }
    const agentName = agents.find(a => a.user_id === agentId)?.name || "this agent";
    if (!window.confirm(`Connect ${userName} with ${agentName}?`)) return;
    await api.post(`/admin/agent-requests/${reqId}/assign`, { agent_id: agentId });
    load();
  };
  const tint = (s) => ({ pending: "border-[#FFD700]/40 text-[#FFD700]", approved: "border-[#F2DC8C]/40 text-neon-lime", denied: "border-[#D4AF37]/40 text-neon-pink" }[s] || "border-white/15 text-muted-foreground");
  return (
    <div className="space-y-6">
      {/* Pending client requests */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          {requests.length} pending client request{requests.length === 1 ? "" : "s"} · {agents.length} active agent{agents.length === 1 ? "" : "s"}
        </div>
        {requests.length === 0 && (
          <div className="card-sharp p-6 text-sm text-muted-foreground">No clients waiting for an agent.</div>
        )}
        {requests.map((r) => (
          <div key={r.request_id} data-testid={`admin-agent-req-${r.request_id}`} className="card-sharp p-4 mb-2 border-[#FFD700]/30">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-display text-sm">{r.user_name || r.user_id} <span className="text-[10px] text-neon-lime">· {r.package_label || "Agent request"}</span></div>
                {r.user_username && <div className="text-[10px] text-neon-cyan">@{r.user_username}</div>}
                <div className="mt-1.5 text-[11px] text-muted-foreground space-y-0.5">
                  {r.event_starts_at && <div>📅 {new Date(r.event_starts_at).toLocaleString()}</div>}
                  {r.event_location && <div>📍 {r.event_location}</div>}
                  {r.contact_phone && <div>📞 {r.contact_phone}</div>}
                  {r.contact_email && <div>✉ {r.contact_email}</div>}
                </div>
                {r.note && <div className="text-xs text-muted-foreground mt-1 italic">"{r.note}"</div>}
                <div className="text-[10px] text-muted-foreground mt-1">Paid · ${Number(r.package_price || 0).toFixed(0)} · Requested {new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  data-testid={`admin-agent-pick-${r.request_id}`}
                  value={picked[r.request_id] || ""}
                  onChange={(e) => setPicked({ ...picked, [r.request_id]: e.target.value })}
                  className="bg-background border border-white/15 text-xs px-2 py-1"
                >
                  <option value="">Pick agent…</option>
                  {agents.map((a) => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
                </select>
                <button
                  data-testid={`admin-agent-assign-${r.request_id}`}
                  onClick={() => assign(r.request_id, r.user_name || r.user_id)}
                  className="btn-neon text-xs"
                >Connect</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Agent roster — direct grant/revoke */}
      <div data-testid="agent-roster" className="card-sharp p-4 sm:p-5 border-[#F2DC8C]/30">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neon-lime flex items-center gap-2">
              <Headset size={12} weight="fill"/> Agent roster
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Grant or revoke Agent status directly. Bypasses the application flow.
            </div>
          </div>
          <input
            data-testid="agent-roster-search"
            value={rosterQ}
            onChange={(e) => { setRosterQ(e.target.value); loadRoster(e.target.value); }}
            placeholder="Search members…"
            className="input-sharp w-full sm:w-64"
          />
        </div>

        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
          Current agents · {roster.agents.length}
        </div>
        {roster.agents.length === 0 && (
          <div className="text-[11px] text-muted-foreground italic mb-3">No agents yet — promote anyone below.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          {roster.agents.map((u) => (
            <div key={u.user_id} data-testid={`roster-agent-${u.user_id}`} className="flex items-center justify-between gap-2 border border-[#F2DC8C]/30 p-2.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{u.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {u.username ? `@${u.username} · ` : ""}{u.email}
                </div>
              </div>
              <button
                data-testid={`roster-revoke-${u.user_id}`}
                onClick={() => setAgentStatus(u.user_id, u.name, false)}
                className="text-[10px] uppercase tracking-widest border border-white/15 text-muted-foreground hover:border-[#D4AF37] hover:text-[#D4AF37] px-2 py-1"
              >Revoke</button>
            </div>
          ))}
        </div>

        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
          Promote a member · top {roster.non_agents.length}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {roster.non_agents.map((u) => (
            <div key={u.user_id} data-testid={`roster-member-${u.user_id}`} className="flex items-center justify-between gap-2 border border-white/10 p-2.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{u.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {u.username ? `@${u.username} · ` : ""}{u.email}
                </div>
              </div>
              <button
                data-testid={`roster-grant-${u.user_id}`}
                onClick={() => setAgentStatus(u.user_id, u.name, true)}
                className="text-[10px] uppercase tracking-widest border border-[#F2DC8C]/40 text-neon-lime hover:bg-[#F2DC8C]/10 px-2 py-1"
              >Grant agent</button>
            </div>
          ))}
          {roster.non_agents.length === 0 && (
            <div className="text-[11px] text-muted-foreground italic">No matches.</div>
          )}
        </div>
      </div>

      {/* Applications */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{rows.length} application{rows.length === 1 ? "" : "s"}</div>
        {rows.length === 0 && <div className="card-sharp p-6 text-sm text-muted-foreground">No agent applications yet.</div>}
        {rows.map((a) => (
          <div key={a.app_id} className="card-sharp p-4 mb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="font-display text-sm flex items-center gap-2"><Headset size={13} className="text-neon-lime"/> {a.user_name}</div>
                <div className="text-[10px] text-muted-foreground mt-1">@{a.user_username} · <a href={`mailto:${a.user_email}`} className="text-neon-cyan">{a.user_email}</a></div>
                {a.phone && <div className="text-[10px] text-muted-foreground">{a.phone}</div>}
                {a.social_link && <div className="text-[10px]"><a href={a.social_link} target="_blank" rel="noopener noreferrer" className="text-neon-cyan">{a.social_link}</a></div>}
              </div>
              <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${tint(a.status)}`}>{a.status}</span>
            </div>
            {a.message && <div className="mt-3 text-sm leading-relaxed text-muted-foreground">{a.message}</div>}
            {a.status === "pending" && (
              <div className="mt-3 flex gap-2">
                <button data-testid={`agent-app-approve-${a.app_id}`} onClick={() => approve(a.app_id)} className="btn-neon text-xs">Approve</button>
                <button data-testid={`agent-app-deny-${a.app_id}`} onClick={() => deny(a.app_id)} className="text-[10px] uppercase tracking-widest text-muted-foreground border border-white/15 px-2 py-1 hover:border-[#D4AF37] hover:text-neon-pink">Deny</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BusinessesAdmin() {
  const [rows, setRows] = useState([]);
  const load = async () => { try { const { data } = await api.get("/admin/businesses"); setRows(data); } catch { /* ignore */ } };
  useEffect(() => { load(); }, []);
  const feature = async (id) => {
    const raw = window.prompt("Feature this business for how many days?", "7");
    if (!raw) return;
    const days = parseInt(raw, 10);
    if (!Number.isFinite(days) || days <= 0) return;
    await api.put(`/admin/businesses/${id}/feature`, { days });
    load();
  };
  const unfeature = async (id) => {
    if (!window.confirm("Remove featured status from this business?")) return;
    await api.put(`/admin/businesses/${id}/unfeature`);
    load();
  };
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {rows.length} business{rows.length === 1 ? "" : "es"} · {rows.filter(r => r.is_featured).length} featured
      </div>
      {rows.length === 0 && <div className="card-sharp p-6 text-sm text-muted-foreground">No business profiles yet.</div>}
      {rows.map((b) => (
        <div key={b.business_id} data-testid={`admin-biz-${b.business_id}`} className={`card-sharp p-4 flex items-center justify-between gap-3 flex-wrap ${b.is_featured ? "border-[#FFD700]/60" : ""}`}>
          <div className="min-w-0 flex-1">
            <div className="font-display text-sm flex items-center gap-2 flex-wrap">
              <Storefront size={13} className="text-[#FFD700]"/>
              <span className="truncate">{b.name}</span>
              {b.is_featured && (
                <span className="text-[8px] uppercase tracking-widest border border-[#FFD700] text-[#FFD700] px-1 py-0.5 flex items-center gap-1">
                  <Star size={8} weight="fill"/> until {new Date(b.featured_until).toLocaleDateString()}
                </span>
              )}
              {b.is_public === false && (
                <span className="text-[8px] uppercase tracking-widest border border-white/15 text-muted-foreground px-1 py-0.5">hidden</span>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1 truncate">
              {b.owner_username && <span className="text-neon-cyan mr-1">@{b.owner_username}</span>}
              {b.location && <span>· {b.location}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {b.is_featured ? (
              <button
                data-testid={`admin-biz-unfeature-${b.business_id}`}
                onClick={() => unfeature(b.business_id)}
                className="text-[9px] uppercase tracking-widest border border-white/15 text-muted-foreground px-2 py-1 hover:border-[#D4AF37] hover:text-neon-pink"
              >Unfeature</button>
            ) : (
              <button
                data-testid={`admin-biz-feature-${b.business_id}`}
                onClick={() => feature(b.business_id)}
                className="text-[9px] uppercase tracking-widest border border-[#FFD700]/60 text-[#FFD700] px-2 py-1 hover:bg-[#FFD700]/10 flex items-center gap-1"
              ><Star size={9} weight="fill"/> Feature</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ConsultationsAdmin() {
  const [rows, setRows] = useState([]);
  const load = async () => { try { const { data } = await api.get("/admin/consultations"); setRows(data); } catch { /* ignore */ } };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.put(`/admin/consultations/${id}/status`, { status });
    load();
  };
  const STATUSES = ["new", "contacted", "booked", "completed", "cancelled"];
  const tint = (s) => ({
    new: "border-[#FFD700] text-[#FFD700]",
    contacted: "border-[#BEE7F0] text-neon-cyan",
    booked: "border-[#F2DC8C] text-neon-lime",
    completed: "border-white/20 text-muted-foreground",
    cancelled: "border-[#D4AF37] text-neon-pink",
  }[s] || "border-white/15 text-muted-foreground");

  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {rows.length} consultation request{rows.length === 1 ? "" : "s"}
      </div>
      {rows.length === 0 && <div className="card-sharp p-6 text-sm text-muted-foreground">No consultation requests yet.</div>}
      {rows.map((r) => (
        <div key={r.consult_id} data-testid={`consult-${r.consult_id}`} className="card-sharp p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-display text-lg flex items-center gap-2"><Briefcase size={14} className="text-[#FFD700]"/> {r.business_name}</div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {r.user_name} · <a href={`mailto:${r.user_email}`} className="text-neon-cyan inline-flex items-center gap-1"><Envelope size={11}/>{r.user_email}</a>
                {r.contact_phone && <span> · {r.contact_phone}</span>}
                {r.industry && <span> · {r.industry}</span>}
              </div>
            </div>
            <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${tint(r.status)}`}>{r.status}</span>
          </div>
          <div className="mt-3 text-sm whitespace-pre-wrap leading-relaxed">{r.goals}</div>
          {r.preferred_time && <div className="mt-2 text-[11px] text-muted-foreground">Preferred window: {r.preferred_time}</div>}
          <div className="mt-3 flex flex-wrap gap-1">
            {STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`consult-${r.consult_id}-set-${s}`}
                onClick={() => setStatus(r.consult_id, s)}
                disabled={s === r.status}
                className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${s === r.status ? "border-white/30 text-foreground" : "border-white/10 text-muted-foreground hover:border-white/30"}`}
              >{s}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [gigs, setGigs] = useState([]);
  const [events, setEvents] = useState([]);

  const load = async () => {
    try {
      const [s, u, g, e] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/gigs"), api.get("/events"),
      ]);
      setStats(s.data); setUsers(u.data); setGigs(g.data); setEvents(e.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role]);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  const del = async (kind, id) => {
    await api.delete(`/admin/${kind}/${id}`);
    load();
  };

  const toggleFeature = async (id) => {
    await api.put(`/admin/gigs/${id}/feature`);
    load();
  };

  const grantPass = async (uid) => {
    const raw = window.prompt("Grant Teams Pass for how many days? (paid in person)", "30");
    if (!raw) return;
    const days = parseInt(raw, 10);
    if (!Number.isFinite(days) || days <= 0) return;
    await api.post(`/admin/users/${uid}/grant-pass`, { days });
    load();
  };
  const revokePass = async (uid) => {
    if (!window.confirm("Revoke this user's Teams Pass?")) return;
    await api.post(`/admin/users/${uid}/revoke-pass`);
    load();
  };
  // One-tap: queue a pending friend request from this user to @777 so the
  // founder can accept it from the normal Friends UI.
  const requestTo777 = async (u) => {
    try {
      const { data } = await api.post(`/admin/friend-request-to-777`, { query: u.email || u.username || u.name });
      const labels = {
        pending_created: "Friend request sent to @777 — accept it from your Friends inbox.",
        pending_reset:   "Existing connection reset to pending — accept it from @777.",
        already_friends: `${u.name || u.username} is already friends with @777.`,
      };
      window.alert(labels[data.status] || `Done: ${data.status}`);
    } catch (e) {
      window.alert(e.response?.data?.detail || "Could not send friend request.");
    }
  };

  return (
    <div className="min-h-screen"
         style={{
           background:
             "radial-gradient(circle at 30% 10%, rgba(212,175,55,0.10) 0%, transparent 55%), linear-gradient(180deg, #0a0703 0%, #050300 100%)",
           color: "#F2DC8C",
         }}>
      {/* Sticky luxury header — matches the InstaShell aesthetic so the
          admin surface no longer looks like the retired site. Full
          internal redesign (subscriptions dashboard, HigherVision
          Credits, etc.) is scheduled as a follow-up task. */}
      <header className="sticky top-0 z-20 backdrop-blur"
              style={{
                background:
                  "linear-gradient(180deg, rgba(10,7,3,0.92) 0%, rgba(5,3,0,0.85) 100%)",
                borderBottom: "1px solid rgba(212,175,55,0.35)",
              }}>
        <div className="flex items-center justify-between px-4 py-3 max-w-5xl mx-auto">
          <a href="/app" aria-label="Back"
             data-testid="admin-back"
             className="w-9 h-9 rounded-full flex items-center justify-center active:scale-90 transition"
             style={{ border: "1px solid #D4AF37", background: "rgba(0,0,0,0.4)" }}>
            <ShieldCheck size={16} weight="bold" className="text-[#FFD700]"/>
          </a>
          <h1 className="font-display text-sm uppercase tracking-[0.28em]"
              style={{ color: "#FFD700" }}>Admin Console</h1>
          <div className="w-9 h-9" aria-hidden />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display text-3xl">Admin Console</h2>
        <div className="inline-flex border border-white/10">
          <button
            data-testid="admin-tab-overview"
            onClick={() => setTab("overview")}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-1 ${tab === "overview" ? "bg-[#D4AF37]/15 text-neon-pink" : "text-muted-foreground hover:text-foreground"}`}
          >
            <ShieldCheck size={12} weight="bold"/> Moderation
          </button>
          <button
            data-testid="admin-tab-payments"
            onClick={() => setTab("payments")}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-1 border-l border-white/10 ${tab === "payments" ? "bg-[#D4AF37]/15 text-neon-pink" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Receipt size={12} weight="bold"/> Payments
          </button>
          <button
            data-testid="admin-tab-consultations"
            onClick={() => setTab("consultations")}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-1 border-l border-white/10 ${tab === "consultations" ? "bg-[#FFD700]/15 text-[#FFD700]" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Briefcase size={12} weight="bold"/> Consults
          </button>
          <button
            data-testid="admin-tab-businesses"
            onClick={() => setTab("businesses")}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-1 border-l border-white/10 ${tab === "businesses" ? "bg-[#FFD700]/15 text-[#FFD700]" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Storefront size={12} weight="bold"/> Businesses
          </button>
          <button
            data-testid="admin-tab-agents"
            onClick={() => setTab("agents")}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-1 border-l border-white/10 ${tab === "agents" ? "bg-[#F2DC8C]/15 text-neon-lime" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Headset size={12} weight="bold"/> Agents
          </button>
          <button
            data-testid="admin-tab-reporters"
            onClick={() => setTab("reporters")}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-1 border-l border-white/10 ${tab === "reporters" ? "bg-[#D4AF37]/15 text-neon-pink" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Newspaper size={12} weight="bold"/> Reporters
          </button>
          <button
            data-testid="admin-tab-featured"
            onClick={() => setTab("featured")}
            className={`px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-1 border-l border-white/10 ${tab === "featured" ? "bg-[#F2DC8C]/15 text-neon-lime" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Gift size={12} weight="bold"/> Featured
          </button>
        </div>
      </div>

      {tab === "payments" && <PaymentsAdmin />}
      {tab === "consultations" && <ConsultationsAdmin />}
      {tab === "businesses" && <BusinessesAdmin />}
      {tab === "agents" && <AgentAppsAdmin />}
      {tab === "reporters" && <ReportersAdmin />}
      {tab === "featured" && <FeaturedAdmin />}

      {tab === "overview" && <>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-6">
          {Object.entries(stats).map(([k,v]) => (
            <div key={k} className="card-sharp p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
              <div className="font-display text-2xl text-neon-pink">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-sharp p-4">
          <div className="text-xs uppercase tracking-widest font-display text-neon-cyan mb-3">Users · {users.length}</div>
          <div className="max-h-96 overflow-y-auto space-y-1">
            {users.map(u => {
              const paid = u.teams_pass_source === "paid";
              const grandfathered = u.teams_pass_source === "grandfathered";
              const isAdmin = u.role === "admin";
              return (
              <div key={u.user_id} className="flex items-center justify-between p-2 border border-white/10 text-xs gap-2">
                <div className="min-w-0">
                  <div className="text-sm flex items-center gap-1.5 flex-wrap">
                    <span className="truncate">{u.name}</span>
                    {u.teams_pass_active && (
                      <span
                        data-testid={`admin-user-pass-${u.user_id}`}
                        title={paid ? `Paid until ${u.teams_pass_until}` : grandfathered ? "Grandfathered (team member)" : "Admin"}
                        className={`text-[8px] uppercase tracking-widest border px-1 py-0.5 flex items-center gap-1 ${
                          paid ? "border-[#F2DC8C]/60 text-neon-lime" :
                          grandfathered ? "border-white/30 text-muted-foreground" :
                          "border-[#D4AF37]/60 text-neon-pink"
                        }`}
                      >
                        <Crown size={8} weight="fill"/>
                        {paid ? "Member" : grandfathered ? "Legacy" : "Admin"}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">{u.email} · {u.role}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!isAdmin && (
                    <button
                      data-testid={`admin-request-777-${u.user_id}`}
                      onClick={() => requestTo777(u)}
                      title="Send friend request to @777 (so admin can accept)"
                      className="text-muted-foreground hover:text-[#F2DC8C] p-1"
                    ><UserPlus size={12} weight="duotone"/></button>
                  )}
                  {!isAdmin && (paid ? (
                    <button
                      data-testid={`admin-revoke-pass-${u.user_id}`}
                      onClick={() => revokePass(u.user_id)}
                      title="Revoke Teams Pass"
                      className="text-muted-foreground hover:text-neon-pink p-1"
                    ><X size={12}/></button>
                  ) : (
                    <button
                      data-testid={`admin-grant-pass-${u.user_id}`}
                      onClick={() => grantPass(u.user_id)}
                      title="Grant Teams Pass (paid in person)"
                      className="text-muted-foreground hover:text-neon-lime p-1"
                    ><Crown size={12} weight={grandfathered ? "regular" : "fill"}/></button>
                  ))}
                  {!isAdmin && (
                    <button data-testid={`admin-del-user-${u.user_id}`} onClick={() => del("users", u.user_id)} className="text-[#D4AF37] p-1"><Trash size={14}/></button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
        <div className="card-sharp p-4">
          <div className="text-xs uppercase tracking-widest font-display text-neon-pink mb-3">Jobs · {gigs.length}</div>
          <div className="max-h-96 overflow-y-auto space-y-1">
            {gigs.map(g => (
              <div key={g.gig_id} className="flex items-center justify-between p-2 border border-white/10 text-xs">
                <div>
                  <div className="text-sm">{g.title}</div>
                  <div className="text-[10px] text-muted-foreground">{g.category} · ${g.pay}{g.is_featured ? " · FEATURED" : ""}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`admin-feature-${g.gig_id}`}
                    onClick={() => toggleFeature(g.gig_id)}
                    title={g.is_featured ? "Unfeature" : "Feature on home feed"}
                    className={g.is_featured ? "text-neon-pink" : "text-muted-foreground hover:text-neon-pink"}
                  >
                    <Star size={14} weight={g.is_featured ? "fill" : "regular"}/>
                  </button>
                  <button data-testid={`admin-del-gig-${g.gig_id}`} onClick={() => del("gigs", g.gig_id)} className="text-[#D4AF37]"><Trash size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="card-sharp p-4">
          <div className="text-xs uppercase tracking-widest font-display text-neon-lime mb-3">Events · {events.length}</div>
          <div className="max-h-96 overflow-y-auto space-y-1">
            {events.map(e => (
              <div key={e.event_id} className="flex items-center justify-between p-2 border border-white/10 text-xs">
                <div>
                  <div className="text-sm">{e.title}</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(e.starts_at).toLocaleDateString()}</div>
                </div>
                <button data-testid={`admin-del-event-${e.event_id}`} onClick={() => del("events", e.event_id)} className="text-[#D4AF37]"><Trash size={14}/></button>
              </div>
            ))}
          </div>
        </div>
      </div>
      </>}
      </div>
    </div>
  );
}
